/**
 * Récupération après l'échec du chargement d'un chunk JavaScript — correctif de la page
 * blanche du tunnel de réservation.
 *
 * <p><b>Défaut constaté en production :</b> les écrans sont chargés en `lazy` (un fichier JS
 * séparé par écran, nommé avec le hash du build). Chaque déploiement remplace les hashes :
 * `BookingPage-C1CxsygF.js` devient `BookingPage-<autre hash>.js`. Un navigateur qui a encore
 * l'ancien `index.html` — onglet ouvert avant le déploiement, ou HTML servi juste avant le
 * basculement des pods — demande alors un chunk qui n'existe plus. Le serveur répond 404,
 * l'import échoue, React n'a rien à rendre et l'utilisateur voit une **page blanche**.
 *
 * <p>C'est exactement le parcours signalé : la recherche et la fiche praticien fonctionnent
 * (leurs chunks étaient déjà en cache), et l'écran blanc apparaît au clic sur « Prendre
 * rendez-vous », parce que `BookingPage` est le seul chunk pas encore téléchargé.
 *
 * <p><b>Pourquoi un rechargement complet et non un nouvel import :</b> le nom du chunk est
 * figé dans l'ancien `index.html`. Réessayer le même import échouera à l'identique. Seul un
 * rechargement récupère un `index.html` à jour, donc les bons hashes.
 *
 * <p><b>Garde-fou anti-boucle :</b> si le rechargement vient d'avoir lieu et que l'échec se
 * reproduit, on ne recharge pas une seconde fois — on laisse l'erreur remonter vers
 * `AppErrorBoundary`, qui affiche un écran explicite. Une boucle de rechargement est pire que
 * l'erreur : elle rend le navigateur inutilisable sans rien dire.
 *
 * @module lib/chunkLoadRecovery
 */

/** Clé de stockage de la date du dernier rechargement automatique. */
export const RELOAD_STAMP_KEY = 'zimed_chunk_reload_at';

/** Fenêtre pendant laquelle un second rechargement est refusé (anti-boucle). */
export const RELOAD_GUARD_MS = 15_000;

/** Vrai si le message d'erreur correspond à un import de module qui a échoué. */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: string } | null | undefined)?.message ?? '';
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message) ||
    // Certains serveurs renvoient `index.html` (200) pour un asset disparu : le navigateur
    // refuse alors le module pour cause de type MIME. C'est le même défaut, autre message.
    /Failed to load module script/i.test(message) ||
    /responded with a MIME type of "text\/html"/i.test(message)
  );
}

interface RecoveryOptions {
  /** Fenêtre de navigation à instrumenter (par défaut `window`). */
  target?: Window;
  /** Stockage du garde-fou (par défaut `sessionStorage`). */
  storage?: Storage;
  /** Fonction de rechargement (par défaut `location.reload`). */
  reload?: () => void;
  /** Horloge injectable, pour les tests. */
  now?: () => number;
}

/**
 * Installe l'écoute de `vite:preloadError` et recharge la page une seule fois.
 *
 * <p>`preventDefault()` est indispensable : sans lui, Vite laisse l'erreur remonter comme
 * `unhandledrejection`, ce qui produit une page blanche avant même que le rechargement
 * puisse s'exécuter.
 *
 * @returns une fonction de désinstallation (utile en test).
 */
export function installChunkLoadRecovery(options: RecoveryOptions = {}): () => void {
  const target = options.target ?? (typeof window !== 'undefined' ? window : undefined);
  if (!target) return () => {};

  const storage =
    options.storage ??
    (() => {
      try {
        return target.sessionStorage;
      } catch {
        // Navigation privée stricte : pas de garde-fou possible, on recharge quand même.
        return undefined;
      }
    })();

  const reload = options.reload ?? (() => target.location.reload());
  const now = options.now ?? (() => Date.now());

  const handler = (event: Event) => {
    // Sans cela, l'échec devient une rejection non gérée et l'écran se vide.
    event.preventDefault();

    const lastRaw = storage?.getItem(RELOAD_STAMP_KEY);
    const last = lastRaw ? Number(lastRaw) : Number.NaN;
    if (Number.isFinite(last) && now() - last < RELOAD_GUARD_MS) {
      // Deuxième échec rapproché : le rechargement n'a rien résolu. On laisse
      // AppErrorBoundary afficher un écran lisible plutôt que boucler.
      return;
    }

    try {
      storage?.setItem(RELOAD_STAMP_KEY, String(now()));
    } catch {
      // Stockage indisponible : on recharge quand même.
    }

    reload();
  };

  target.addEventListener('vite:preloadError', handler);
  return () => target.removeEventListener('vite:preloadError', handler);
}
